/**
 * v7.16 — Config Centralizada Cliente.
 *
 * Wrapper que substitui `db.config.get/.put` para chaves administrativas
 * globais. Lê/grava via `/api/config` no backend (Mongo doc único),
 * mantendo o Dexie apenas como cache para uso offline.
 *
 * Fluxo:
 *   loadServerConfig() → fetch GET → atualiza cache Dexie → retorna obj
 *   saveServerConfig({key:value, ...}) → PUT API → atualiza Dexie
 *
 * Compatibilidade: chaves NÃO conhecidas pelo backend (whitelist) continuam
 * apenas no Dexie (ex.: `deviceNickname`, `activePreset`).
 */
import db from '@/db';
import { authedFetch } from '@/lib/adminAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const CACHE_KEY = '__serverConfig__';

// Whitelist: chaves que o backend conhece e gerencia globalmente.
// Mantenha em sincronia com PUBLIC_KEYS + SENSITIVE_KEYS do routes/config.py.
export const SERVER_MANAGED_KEYS = new Set([
  'restaurantName', 'restaurantPhone', 'restaurantAddress',
  'deliveryFee', 'minOrderValue', 'pixKey',
  'menuPublicUrl', 'zapiInstanceId',
  'mpEnvironment', 'mpPublicKey',  // v7.22
  'firebaseApiKey', 'firebaseAuthDomain', 'firebaseDatabaseURL',
  'firebaseProjectId', 'firebaseStorageBucket', 'firebaseMessagingSenderId',
  'firebaseAppId', 'deviceGroup',
  'requireOpenSession', 'trainingMode', 'buffetPricePerKg',
  'restaurantGreeting', 'coffeeComboPrice',  // v7.18
  'deliveryPaused',  // v7.98 — pausa global do delivery
  // sensíveis (não retornam valor no GET, mas podem ser PUT)
  'zapiInstanceToken', 'zapiClientToken', 'mpAccessToken',
]);

/**
 * Busca config do servidor. Em caso de falha de rede, devolve o último
 * cache do Dexie (graceful offline).
 */
export async function loadServerConfig({ useCacheOnFail = true } = {}) {
  if (!API_URL) {
    if (useCacheOnFail) return _readCache();
    throw new Error('REACT_APP_BACKEND_URL ausente');
  }
  // v7.78 — Timeout explícito: sem ele, um fetch travado (rede móvel ruim)
  // pendurava isPixConfigured() e deixava o checkout preso em
  // "Gerando pagamento..." pra sempre.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);
  try {
    const r = await fetch(`${API_URL}/api/config`, { method: 'GET', signal: controller.signal });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const body = await r.json();
    // body = { config: {...}, flags: {...}, updatedAt: '...' }
    // Atualiza cache local (apenas o subset público; flags ficam separados).
    try {
      await db.config.put({ key: CACHE_KEY, value: body, cachedAt: Date.now() });
    } catch {/* cache opcional */}
    return body;
  } catch (err) {
    if (!useCacheOnFail) throw err;
    // eslint-disable-next-line no-console
    console.warn('[serverConfig] GET falhou, usando cache local:', err?.message || err);
    return _readCache();
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Persiste alteração no servidor. Aceita objeto com 1+ chaves.
 * Atualiza cache imediatamente após sucesso.
 */
export async function saveServerConfig(updates) {
  if (!updates || typeof updates !== 'object' || Object.keys(updates).length === 0) {
    throw new Error('saveServerConfig: payload vazio');
  }
  if (!API_URL) throw new Error('REACT_APP_BACKEND_URL ausente');
  // Filtra apenas chaves conhecidas pelo backend (não pollui a API).
  const filtered = {};
  for (const [k, v] of Object.entries(updates)) {
    if (SERVER_MANAGED_KEYS.has(k)) filtered[k] = v;
  }
  if (Object.keys(filtered).length === 0) {
    throw new Error('Nenhuma chave conhecida pelo servidor no payload');
  }
  const r = await authedFetch(
    `${API_URL}/api/config`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filtered),
    },
    { message: 'Digite o PIN admin para gravar essas configurações no servidor.' },
  );
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(`HTTP ${r.status}: ${txt.slice(0, 200)}`);
  }
  const body = await r.json();
  try {
    await db.config.put({ key: CACHE_KEY, value: body, cachedAt: Date.now() });
  } catch {/* opcional */}
  return body;
}

async function _readCache() {
  try {
    const row = await db.config.get(CACHE_KEY);
    return row?.value || { config: {}, flags: {}, updatedAt: null };
  } catch {
    return { config: {}, flags: {}, updatedAt: null };
  }
}

/** Conveniência: lê uma chave única do cache (sync após loadServerConfig prévio). */
export async function getServerConfigKey(key) {
  const cache = await _readCache();
  return cache?.config?.[key];
}
